/**
 * App-Routing mit TanStack Router.
 *
 * Struktur:
 *   /login                  — öffentlich
 *   /                       — Dashboard (auth-required, Layout)
 *   /calendar, /sessions, … — alle anderen (auth-required, Layout)
 *
 * Auth-Guard: Vor geschützten Routen wird `useAuth()` geprüft.
 * Ist User null und isLoading false → redirect zu /login.
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { AppLayout } from "./components/AppLayout";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { LocationsPage } from "./pages/LocationsPage";
import { RoomsPage } from "./pages/RoomsPage";
import { ResourcesPage } from "./pages/ResourcesPage";
import { TrainersPage } from "./pages/TrainersPage";
import { SessionsPage } from "./pages/SessionsPage";
import { CalendarPage } from "./pages/CalendarPage";
import { AnnualLocationReportPage } from "./pages/AnnualLocationReportPage";
import { UsersPage } from "./pages/UsersPage";
import { StubPage } from "./pages/StubPage";
import { useAuth } from "./lib/auth";
import { t } from "./locales/de";
import { parseSessionSearch } from "./lib/sessionNavigation";

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// ----- /login (öffentlich) -----
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

// ----- Auth-Wrapper -----
function ProtectedLayout() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        {t.common.loading}
      </div>
    );
  }

  // Redirect darf NICHT in `component` (throw) gemacht werden — in TanStack Router
  // v1+ muss beforeLoad auf der Route genutzt werden. Hier nur rendern, der
  // beforeLoad auf `protectedRoute` kümmert sich um den Redirect.
  if (!user) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        {t.common.loading}
      </div>
    );
  }

  return <AppLayout />;
}

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  component: ProtectedLayout,
  // Auth-Guard: vor dem Render prüfen ob User eingeloggt ist. Wenn nicht → /login.
  // beforeLoad läuft VOR dem Component-Render, daher kein "Something went wrong".
  // WICHTIG: `throw redirect()` muss DIREKT im beforeLoad geworfen werden,
  // nicht in einem .then() (sonst fängt TanStack es als Error → ErrorBoundary).
  beforeLoad: ({ location }) => {
    // Wir holen den Auth-Status VOR dem Component-Render. Wenn der Server
    // einen 401 antwortet, leiten wir direkt zu /login um. Der redirect-Throw
    // darf NICHT in den try/catch-Block (s. unten).
    let redirectError: unknown = null;
    return (async () => {
      try {
        const r = await fetch("/api/auth/me", { credentials: "include" });
        if (r.status === 401) {
          // redirect() throwt synchron — das wird im async-Kontext zum
          // rejected Promise. Wir fangen es unten NICHT ab (s. comment).
          throw redirect({
            to: "/login",
            search: { redirect: location.href },
          });
        }
        return null;
      } catch (e) {
        // Wenn es ein redirect-Error ist, weiterwerfen — TanStack Router
        // prüft das mit isRedirect() aus @tanstack/router-core.
        // Wir markieren den Error hier und werfen ihn NACH dem catch-Block
        // erneut, damit TanStack ihn als Navigation behandelt.
        redirectError = e;
        return null;
      } finally {
        if (redirectError) throw redirectError;
      }
    })();
  },
});

// ----- Geschützte Routen -----
const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/",
  component: DashboardPage,
});

const calendarRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/calendar",
  component: CalendarPage,
});

const sessionsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/sessions",
  validateSearch: parseSessionSearch,
  component: SessionsPage,
});

const locationsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/locations",
  component: LocationsPage,
});

const roomsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/rooms",
  component: RoomsPage,
});

const resourcesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/resources",
  component: ResourcesPage,
});

const trainersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/trainers",
  component: TrainersPage,
});

const usersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/users",
  component: UsersPage,
});

const importRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/import",
  component: () => (
    <StubPage
      title={t.nav.import}
      planned="Oracle-XLSX-Import: 7-Schritt-Wizard (Upload, Spalten-Mapping, Vorschau, Konflikt-Check, Standort-Mapping, Commit, Folge-Aktion)."
    />
  ),
});

const reportsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/reports",
  component: AnnualLocationReportPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  protectedRoute.addChildren([
    dashboardRoute,
    calendarRoute,
    sessionsRoute,
    locationsRoute,
    roomsRoute,
    resourcesRoute,
    trainersRoute,
    usersRoute,
    importRoute,
    reportsRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
